/**
 * OnboardingModal - Multi-step first-login information collection
 *
 * Collects user info in 4 steps:
 * 1. Nickname - "What should I call you?" (required)
 * 2. Location - Single autocomplete search using OpenStreetMap (required)
 * 3. Timezone - "What's your timezone?" (required)
 * 4. Context - Work setup, activities, locations (optional, can skip)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { supabase } from "../supabaseClient";
import moment from "moment-timezone";
import Select from "react-select";
import "./OnboardingModal.css";

const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";

const TOTAL_STEPS = 4;



const STEP_CONFIG = [
  {
    id: 1,
    icon: "👋",
    title: "Welcome to Zoomey",
    question: "What should I call you?",
    placeholder: "Your name or nickname",
    field: "nickname",
    subtext: "I'm here to create mental space and make your day easier to run.",
    optional: false,
  },
  {
    id: 2,
    icon: "📍",
    title: "Almost there!",
    question: "Where are you based?",
    placeholder: "Search your city or location...",
    field: "location",
    subtext: "This helps me keep reminders and planning grounded in your local context.",
    optional: false,
  },
  {
    id: 3,
    icon: "🕐",
    title: "One last thing!",
    question: "What's your timezone?",
    placeholder: "Select timezone...",
    field: "timezone",
    subtext: "So your reminders, follow-ups, and schedules land at the right moment.",
    optional: false,
  },
  {
    id: 4,
    icon: "💭",
    title: "Work rhythm (optional)",
    question: "What does your typical week look like?",
    placeholder: "",
    field: "routine",
    optional: true,
  },
];

export default function OnboardingModal({ show, onComplete, userId, userName }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [nickname, setNickname] = useState("");
  const [location, setLocation] = useState("");
  const [locationData, setLocationData] = useState(null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const searchTimeoutRef = useRef(null);
  const locationInputRef = useRef(null);
  
  // Step 4 state - single smart text input
  const [routineDescription, setRoutineDescription] = useState("");

  // Timezone options for react-select
  const timezoneOptions = moment.tz.names().map((tz) => ({
    value: tz,
    label: `${tz} (UTC${moment.tz(tz).format("Z")})`,
  }));

  // Pre-fill nickname with first name from Google
  useEffect(() => {
    if (userName && show) {
      const firstName = userName.split(" ")[0];
      setNickname(firstName);
    }
  }, [userName, show]);

  // Auto-detect timezone on mount
  useEffect(() => {
    if (show && !timezone) {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedTz && moment.tz.names().includes(detectedTz)) {
        setTimezone(detectedTz);
      }
    }
  }, [show, timezone]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search location using OpenStreetMap Nominatim (free, no API key)
  const searchLocation = async (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      const data = await response.json();
      
      const suggestions = data.map((item) => ({
        display_name: item.display_name,
        // Check multiple address fields - Indian cities often come as state_district or county
        city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || item.address?.state_district || item.address?.county || item.name || "",
        state: item.address?.state || item.address?.region || "",
        country: item.address?.country || "",
        lat: item.lat,
        lon: item.lon,
      }));
      
      setLocationSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (err) {
      console.error("[OnboardingModal] Location search error:", err);
      setLocationSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    setLocationData(null);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 300);
  };

  const handleSelectLocation = async (suggestion) => {
    // Build a clean display name
    const parts = [];
    if (suggestion.city) parts.push(suggestion.city);
    if (suggestion.state) parts.push(suggestion.state);
    if (suggestion.country) parts.push(suggestion.country);
    const displayName = parts.join(", ") || suggestion.display_name;

    setLocation(displayName);
    setLocationData({
      city: suggestion.city,
      state: suggestion.state,
      country: suggestion.country,
      display_name: displayName,
    });
    setShowSuggestions(false);
    setError("");

    // Auto-detect timezone from location coordinates
    if (suggestion.lat && suggestion.lon) {
      try {
        // Using Open-Meteo API (completely free, no API key, no limits)
        const tzResponse = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${suggestion.lat}&longitude=${suggestion.lon}&current_weather=false&timezone=auto`
        );
        const tzData = await tzResponse.json();

        console.log("[OnboardingModal] Timezone API response:", tzData);

        // Open-Meteo returns timezone field with IANA timezone
        const detectedTz = tzData?.timezone;

        if (detectedTz && moment.tz.names().includes(detectedTz)) {
          console.log("[OnboardingModal] Setting timezone to:", detectedTz);
          setTimezone(detectedTz);
        } else {
          console.warn("[OnboardingModal] Invalid timezone received:", detectedTz);
        }
      } catch (err) {
        console.error("[OnboardingModal] Timezone auto-detect error:", err);
        // Keep existing timezone (browser auto-detect) if API fails
      }
    }
  };

  const getCurrentStepConfig = () => STEP_CONFIG[currentStep - 1];

  const validateCurrentStep = () => {
    const step = getCurrentStepConfig();
    
    if (step.id === 1 && !nickname.trim()) {
      setError("Please enter a name");
      return false;
    }
    
    if (step.id === 2 && !locationData) {
      setError("Please select a location from the suggestions");
      return false;
    }
    
    if (step.id === 3 && !timezone) {
      setError("Please select your timezone");
      return false;
    }
    
    setError("");
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError("");
    }
  };

  // Core submission logic (shared by submit and skip)
  const submitOnboarding = useCallback(async (skipped = false) => {
    setIsLoading(true);
    setError("");

    try {
      // Build request body with all data
      const requestBody = {
        user_id: userId,
        // Step 1: Nickname
        display_name: nickname.trim(),
        nickname: nickname.trim(),
        name: nickname.trim(),
        // Step 2: Location
        country: locationData?.country || null,
        state: locationData?.state || null,
        city: locationData?.city || null,
        location: locationData?.display_name || null,
        // Step 3: Timezone
        time_zone: timezone,
        // Step 4: Smart routine description (single text)
        routine_description: skipped ? null : (routineDescription.trim() || null),
        // Flags
        onboarding_state: "completed",
        save_to_memory: true, // Save all data to memory
      };

      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!data.success) {
        // Fallback: try updating via Supabase directly
        if (data.needs_migration) {
          console.warn("[OnboardingModal] Migration needed, using direct update");
          await supabase
            .from("profiles")
            .update({
              name: nickname.trim(),
              nickname: nickname.trim(),
              location: locationData?.display_name || null,
              time_zone: timezone,
            })
            .eq("id", userId);
        } else {
          throw new Error(data.error || "Failed to save profile");
        }
      }

      // Complete onboarding with all collected data
      onComplete({
        nickname: nickname.trim(),
        country: locationData?.country,
        state: locationData?.state,
        city: locationData?.city,
        location: locationData?.display_name,
        timezone: timezone,
        routineDescription: skipped ? null : routineDescription.trim(),
      });

      // Refresh page to load user context into chat
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("[OnboardingModal] Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [nickname, locationData, timezone, routineDescription, userId, onComplete]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    // Only allow submit on final step (Step 4)
    if (currentStep !== TOTAL_STEPS) {
      return;
    }

    // Final validation for required steps
    if (!nickname.trim()) {
      setError("Please enter a name");
      return;
    }
    if (!locationData) {
      setError("Please select a location from the suggestions");
      return;
    }
    if (!timezone) {
      setError("Please select your timezone");
      return;
    }
    
    // Step 4 is optional, no validation needed
    await submitOnboarding(false);
  }, [currentStep, nickname, locationData, timezone, submitOnboarding]);

  // Handle Enter key on individual inputs (step 1 & 2)
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (!isNextDisabled() && currentStep < TOTAL_STEPS) {
        handleNext();
      }
    }
  };

  // Prevent form-level Enter key from auto-submitting
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      // Only allow submit on step 3 when user explicitly presses Enter and form is valid
      if (currentStep === TOTAL_STEPS && !isNextDisabled() && !isLoading) {
        handleSubmit(e);
      } else if (currentStep < TOTAL_STEPS && !isNextDisabled()) {
        handleNext();
      }
    }
  };

  const stepConfig = getCurrentStepConfig();

  // Custom styles for timezone select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "var(--input-bg, #f9fafb)",
      borderColor: state.isFocused ? "var(--accent-color, #009689)" : "var(--border-color, #e5e7eb)",
      borderWidth: "2px",
      borderRadius: "12px",
      minHeight: "52px",
      padding: "2px 8px",
      fontSize: "16px",
      boxShadow: "none",
      outline: "none",
      "&:hover": {
        borderColor: "var(--accent-color, #009689)",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px",
    }),
    input: (base, state) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: "var(--text-primary, #1f2937)",
      caretColor: "var(--accent-color, #009689)",
      // Hide input when value is selected and menu is closed
      ...(state.hasValue && !state.selectProps.menuIsOpen ? {
        position: "absolute",
        width: "1px",
        height: "1px",
        opacity: 0,
        overflow: "hidden",
      } : {}),
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--card-bg, #ffffff)",
      border: "1px solid var(--border-color, #e5e7eb)",
      borderRadius: "12px",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "var(--accent-color, #009689)" : "transparent",
      color: state.isFocused ? "white" : "var(--text-primary, #1f2937)",
      cursor: "pointer",
      padding: "12px 16px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text-primary, #1f2937)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--text-primary, #1f2937)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--text-muted, #9ca3af)",
    }),
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form.Control
            type="text"
            placeholder={stepConfig.placeholder}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onKeyDown={handleInputKeyDown}
            className="onboarding-input"
            autoFocus
            maxLength={50}
          />
        );
      case 2:
        return (
          <div className="location-autocomplete" ref={locationInputRef}>
            <div className="location-input-wrapper">
              <Form.Control
                type="text"
                placeholder={stepConfig.placeholder}
                value={location}
                onChange={handleLocationInputChange}
                onKeyDown={handleInputKeyDown}
                onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                className="onboarding-input"
                autoFocus
              />
              {isSearching && (
                <div className="location-searching">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </div>
            
            {showSuggestions && locationSuggestions.length > 0 && (
              <ul className="location-suggestions">
                {locationSuggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => handleSelectLocation(suggestion)}
                    className="location-suggestion-item"
                  >
                    <span className="suggestion-icon">📍</span>
                    <span className="suggestion-text">{suggestion.display_name}</span>
                  </li>
                ))}
              </ul>
            )}
            
            {locationData && (
              <div className="location-selected">
                ✓ Selected: {locationData.display_name}
              </div>
            )}
          </div>
        );
      case 3:
        return (
          <div className="timezone-container">
            <Select
              value={timezoneOptions.find((opt) => opt.value === timezone) || null}
              onChange={(selected) => setTimezone(selected?.value || "")}
              options={timezoneOptions}
              isClearable
              isSearchable
              placeholder={stepConfig.placeholder}
              styles={selectStyles}
              menuPlacement="auto"
              classNamePrefix="tz-select"
              blurInputOnSelect
            />
          </div>
        );
      case 4:
        return (
          <div className="context-container">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="e.g., client calls Tue/Thu afternoons, deep work mornings, invoicing Fridays, gym Mon/Wed/Fri..."
              value={routineDescription}
              onChange={(e) => setRoutineDescription(e.target.value)}
              className="onboarding-input onboarding-textarea"
              maxLength={500}
            />
            <p className="context-hint">
              Share it naturally — I'll remember what matters and reduce repeat planning.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  // Check if current step is valid for Next button
  const isNextDisabled = () => {
    if (isLoading) return true;
    if (currentStep === 1 && !nickname.trim()) return true;
    if (currentStep === 2 && !locationData) return true;
    if (currentStep === 3 && !timezone) return true;
    // Step 4 is optional, never disabled
    return false;
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      className="onboarding-modal"
    >
      <Modal.Body className="onboarding-modal-body">
        {/* Step Indicator */}
        <div className="step-indicator">
          {STEP_CONFIG.map((step) => (
            <div
              key={step.id}
              className={`step-dot ${currentStep >= step.id ? "active" : ""}`}
            />
          ))}
        </div>

        <div className="onboarding-icon">
          <span role="img" aria-label="icon">
            {stepConfig.icon}
          </span>
        </div>

        <h2 className="onboarding-title">{stepConfig.title}</h2>
        <p className="onboarding-question">{stepConfig.question}</p>

        <Form onSubmit={(e) => e.preventDefault()} onKeyDown={handleFormKeyDown}>
          <Form.Group>
            {renderStepContent()}
            {error && <div className="onboarding-error">{error}</div>}
          </Form.Group>

          {/* Navigation Buttons */}
          <div className="onboarding-navigation">
            {currentStep > 1 && (
              <button
                type="button"
                className="onboarding-back"
                onClick={handleBack}
                disabled={isLoading}
              >
                ← Back
              </button>
            )}

            <div className="onboarding-nav-right">
              {currentStep < TOTAL_STEPS ? (
                <button
                  type="button"
                  className="onboarding-submit"
                  onClick={handleNext}
                  disabled={isNextDisabled()}
                >
                  Next →
                </button>
              ) : (
                <button
                  type="button"
                  className="onboarding-submit"
                  onClick={handleSubmit}
                  disabled={isNextDisabled()}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Setting up...
                    </>
                  ) : (
                    "Start with a clear plan"
                  )}
                </button>
              )}
            </div>
          </div>
        </Form>

        <p className="onboarding-privacy">
          {currentStep === 1
            ? "I'm here to reduce mental clutter and help you focus on what matters."
            : currentStep === 2
            ? "This helps me keep your planning grounded in your real day."
            : currentStep === 3
            ? "This keeps reminders and schedules aligned with your timezone."
            : "This helps me support your workflow with calmer, more relevant suggestions. You can update this anytime."}
        </p>
      </Modal.Body>
    </Modal>
  );
}
